import { DEFAULT_ON_FEATURES } from "@/config/default-on-features.generated";
import type { FeatureKey, MeResponse, SpecialtyPack } from "@/features/auth/types";

/**
 * Vérifie si l'utilisateur authentifié possède une permission (codename
 * format `domain.action`).
 * Superuser et staff éditeur GTS (plan éditeur : pleine autorité sur les
 * cliniques, accordée aussi côté back) bypassent tout. Sans user, false.
 */
export function can(me: MeResponse | null, permission: string): boolean {
  if (!me) return false;
  if (me.is_superuser || me.is_platform_staff) return true;
  return me.permissions.includes(permission);
}

export function canAny(me: MeResponse | null, permissions: string[]): boolean {
  return permissions.some((p) => can(me, p));
}

export function canAll(me: MeResponse | null, permissions: string[]): boolean {
  return permissions.every((p) => can(me, p));
}

/**
 * True si une clinique active est sélectionnée. Sans clinique active, aucun
 * rattachement n'est possible : les actions de création d'entités affiliées
 * (patient, utilisateur, boutique) doivent être masquées.
 */
export function hasActiveClinic(me: MeResponse | null): boolean {
  return !!me?.active_organisation_id;
}

/**
 * Une suppression ORDINAIRE peut-elle aboutir sur cet élément ?
 *
 * Faux = l'élément porte des données rattachées et le serveur refusera. Les
 * écrans ne s'en servent plus pour MASQUER l'action — l'action muette laissait
 * chercher un bouton absent (sur les fiches patient, 2,4 % seulement étaient
 * supprimables). Ils s'en servent pour choisir la fenêtre : confirmation
 * ordinaire, ou `DeletionBlockedDialog` qui nomme ce qui retient l'élément.
 *
 * Le super éditeur GTS reste du côté « confirmation » même sur un élément
 * rattaché : c'est le refus de son DELETE qui ouvre la proposition de cascade
 * (ADR 0015), captée sur la réponse HTTP.
 *
 * `recordAllows` = ce que dit l'enregistrement (`can_delete`, et tout autre
 * verrou lié à ses rattachements). `can_cascade` vient du serveur, jamais
 * d'une déduction front (INV-6 — le back revérifie de toute façon).
 */
export function deletionAllowed(me: MeResponse | null, recordAllows: boolean): boolean {
  return recordAllows || !!me?.can_cascade;
}

/**
 * True si le compte appartient à l'éditeur GoldenTech : personnel plateforme
 * ou superuser technique. Sert à réserver les surfaces internes GTS (aide au
 * paramétrage RBAC, comptes d'intervention) que l'abonné n'a pas à voir.
 */
export function isGtsEditor(me: MeResponse | null): boolean {
  return !!me?.is_platform_staff || !!me?.is_superuser;
}

/**
 * True si la clinique active expose les modules « clinique générale » (dossier
 * médical, constantes vitales). Affichés en clinique générale ; masqués par
 * défaut en clinique optique (opt-in `display_config.medical_record`) — ainsi
 * les cliniques optiques existantes restent strictement inchangées.
 */
export function showsGeneralClinical(me: MeResponse | null): boolean {
  if (!me) return false;
  return hasPack(me, "general") || me.active_display_config?.medical_record === true;
}

/**
 * True si la clinique active expose les modules ophtalmologiques (ordonnances
 * optiques + chaînage consultation → ordonnance). Réservé à la spécialité
 * « optique ». Les cliniques existantes sont en « optique » → comportement
 * inchangé ; une clinique générale ne voit pas ces modules.
 */
export function showsOptical(me: MeResponse | null): boolean {
  return hasPack(me, "optique");
}

/**
 * True si la nouveauté `feature` est activée pour la clinique active.
 * Éteinte par défaut : un abonné qui n'a rien activé garde l'écran d'avant.
 * Exception : un module actif par défaut (`DEFAULT_ON_FEATURES`, ADR 0063)
 * reste actif tant que `me` ne le nomme pas dans `disabled_features` — un
 * `me` ancien, mis en cache hors ligne, le garde donc allumé.
 * Ne remplace pas `can()` — une nouveauté activée reste soumise aux droits.
 */
export function hasFeature(me: MeResponse | null, feature: FeatureKey): boolean {
  if (!me) return false;
  const config = me.active_display_config;
  if (config?.features?.includes(feature) === true) return true;
  return (
    DEFAULT_ON_FEATURES.includes(feature) &&
    !(config?.disabled_features ?? []).includes(feature)
  );
}

/**
 * True si la clinique active vend des actes (ADR 0047) : catalogue « Actes et
 * tarifs » et facturation depuis la consultation. D'office hors optique ; une
 * clinique d'optique l'obtient en activant la nouveauté `billable_acts`.
 * Même règle que le serveur (`apps.inventory.services.acts_enabled`).
 */
export function showsActs(me: MeResponse | null): boolean {
  if (!me) return false;
  return hasPack(me, "general") || hasFeature(me, "billable_acts");
}

/**
 * True si la clinique active a le pack de spécialité `pack`. Une clinique peut
 * en cumuler plusieurs (polyclinique : médecine générale ET optique). Le
 * serveur applique la même règle à ses routes (`RequiresPack`).
 */
export function hasPack(me: MeResponse | null, pack: SpecialtyPack): boolean {
  if (!me) return false;
  // Réponse d'une ancienne version : la spécialité unique tient lieu de pack.
  return (me.active_packs ?? [me.active_specialty]).includes(pack);
}
